import { createSelector, createStructuredSelector } from "reselect"

import { fromJS } from "immutable"

import { issueAdderExtraFields } from "shared/profiles/constants/constants"
import {
    selectAlignmentTypes,
    selectIsIssueAlignmentsLoading,
    selectIssueAlignmentIssues,
    selectUpdatingIssueAlignments,
} from "shared/stakeholder/selectors"

export const selectIssueSlice = state => state.issueAdder
export const selectProfileId = (_, props) => props.profileId
export const selectDataType = (_, props) => props.dataType

export const selectIssueAdderForProfile = createSelector(
    selectDataType,
    selectProfileId,
    selectIssueSlice,
    (qdt, id, slice) => slice && slice.get(`${qdt.value}-${id}`)
)

export const createSelectorFromIssueAdder = attr => createSelector(
    selectIssueAdderForProfile,
    (issueAdder) => {
        const onIssueAdder = issueAdder && issueAdder.get(attr)
        return (onIssueAdder && onIssueAdder.toJS) ? onIssueAdder.toJS() : onIssueAdder
    }
)

export const selectIssues = createSelector(
    selectIssueAdderForProfile,
    issueAdder => (
        // Filter the issues by if they were not optimistically removed
        issueAdder && (
            issueAdder.get("issues")
                .filter(issue => !issue.get(issueAdderExtraFields.tentativelyRemoved))
                // format the issues to show parent name as well as name
                .map((immutableIssue) => {
                    if (!immutableIssue.has("displayText")) {
                        // issues from issues props have parent name stored in parent_name
                        // while issues added from the select component have issues stored in `_extra.parent_name`
                        const parentName = immutableIssue.getIn(["_extra", "parent_name"]) || immutableIssue.get("parent_name")
                        return parentName
                            ? immutableIssue.set("displayText", `${immutableIssue.get("name")} (${parentName})`)
                            : immutableIssue.set("displayText", immutableIssue.get("name"))
                    }
                    return immutableIssue
                })
        )
    )
)

/**
 * Create a selector that returns a mapping of issue IDs to the contents of the given field
 * for the issue with the corresponding ID.
 *
 * @name createSelectIssueToFieldMapping
 * @function
 * @param {String} fieldName   - name of the field to map issue IDs to
 *
 * @returns {Object}  returns an object of the shape { issueID: field contents }, with an entry for each issue,
 *                      where the field contents are retrieved from the fieldName field on the issue (or it _extra object)
 */
export const createSelectIssueToFieldMapping = fieldName => createSelector(
    selectIssues,
    issues => issues && fromJS(issues.reduce((accObj, issue) => {
        // The full-hierarchy issue adder creates "skeleton" issues for issues
        // retrieved from dehydrate_extra_ancestors; such issues do not have _extra
        // fields and therefore store "ancestor_ids" and "parent_name" directly on their objects
        // Get the given field from the appropriate place
        const fieldContents = (issue.get("_extra") && issue.getIn(["_extra", fieldName])) || issue.get(fieldName)
        return { ...accObj, [issue.get("id")]: fieldContents }
    }, {}))
)

export const selectAncestorIds = createSelectIssueToFieldMapping("ancestor_ids")
export const selectParentNames = createSelectIssueToFieldMapping("parent_name")

export const selectIsLoading = createSelectorFromIssueAdder("loading")
export const selectIsLoadingAlignments = createSelectorFromIssueAdder("isLoadingAlignments")
export const selectHasError = createSelectorFromIssueAdder("error")
export const selectNoResultsType = (_, props) => props.dataType && props.dataType.singular
export const selectRegion = (_, props) => props.region


export const issueAdderConnection = createStructuredSelector({
    ancestorIds: selectAncestorIds,
    issues: selectIssues,
    error: selectHasError,
    loading: selectIsLoading,
    noResultsType: selectNoResultsType,
    parentNames: selectParentNames,
    region: selectRegion,
    issueAlignments: selectIssueAlignmentIssues,
    alignmentTypes: selectAlignmentTypes,
    isLoadingAlignments: selectIsIssueAlignmentsLoading,
    updatingAlignments: selectUpdatingIssueAlignments,
})
